import { useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, ScanLine, Check, ShieldCheck, Pencil, Info } from "lucide-react";
import { fieldList } from "@/lib/text";

const MARK = {
  decoded: { label: "Decoded", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  corrected: { label: "Corrected by a person", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  manual: { label: "Entered by a person", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  confirmed: { label: "Checked by a person", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};
const HUMAN = ["corrected", "manual", "confirmed"];

// THE VIN DECODE IS A STARTING POINT, NOT THE TRUTH — his words. Every field the decode can fill
// is here, every one is editable, every one says where its value came from, and what the decode
// said is kept beside a corrected value so the disagreement is visible instead of lost.
//
// HIS RULING ON THE TICK: the engine and the transmission are never inside a bulk tick — "that's
// where the decode gets it wrong". They are grouped apart and ticked one at a time; the server
// refuses a batch that includes them, so this is the shape of a rule rather than the rule itself.
export const VinDecodedFields = ({ donor, fields, form, setForm, onSaved, canEdit }) => {
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const srcs = donor.field_sources || {};
  const meta = donor.field_source_meta || {};
  const said = (donor.vin_decode || {}).fields || {};

  const valueOf = (f) => (f.stored === "donor" ? (form[f.key] ?? "") : ((form.vin_extra || {})[f.key] ?? ""));
  const unchecked = useMemo(() => fields.filter((f) => String(valueOf(f) ?? "").trim()
    && !HUMAN.includes(srcs[f.key])), [fields, form, srcs]);           // eslint-disable-line react-hooks/exhaustive-deps
  // What a single tick may cover: everything except the engine and the gearbox.
  const bulk = unchecked.filter((f) => !f.own_tick);
  const groups = useMemo(() => {
    const out = [];
    fields.forEach((f) => {
      let g = out.find((x) => x.group === f.group);
      if (!g) out.push((g = { group: f.group, label: f.group_label, fields: [] }));
      g.fields.push(f);
    });
    return out;
  }, [fields]);

  const setVal = (f, v) => setForm((s) => (f.stored === "donor"
    ? { ...s, [f.key]: v }
    : { ...s, vin_extra: { ...s.vin_extra, [f.key]: v } }));

  const run = async (path, body, okMsg) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/donors/${donor.id}/${path}`, body);
      if (data.applied || data.protected) setReport(data);
      toast.success(okMsg(data));
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="vin-decoded-block" className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <ScanLine className="w-4 h-4 text-[#E0781A]" />
        <span className="font-semibold text-sm">From the VIN decode</span>
        {unchecked.length > 0 && (
          <Badge data-testid="vin-unchecked-count" className="rounded-lg bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-mono uppercase">
            {unchecked.length} not checked by a person
          </Badge>
        )}
      </div>
      <p className="text-[11px] text-slate-500 mb-3">
        The decode is a starting point, not the truth. Change anything here — it is kept as your
        correction, with what the decode said beside it, and no re-decode will ever overwrite it.
      </p>

      <div className="space-y-4">
        {groups.map((g) => (
          <div key={g.group} data-testid={`vin-group-${g.group}`}>
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1.5 pb-1 border-b border-slate-100">
              {g.label}
            </div>
            {/* ONE PER LINE. A two-up grid was tried and rejected: the value box and its tick
                button shared a half-width cell and "2012" rendered as "201" — a value you cannot
                read is worse than a longer scroll. */}
            <div className="space-y-2.5">
              {g.fields.map((f) => {
                const live = valueOf(f);
                const mark = srcs[f.key];
                const m = MARK[mark];
                const filled = !!String(live ?? "").trim();
                const disagrees = said[f.key] && String(said[f.key]).trim().toLowerCase() !== String(live || "").trim().toLowerCase();
                return (
                  <div key={f.key} data-testid={`vin-field-${f.key}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{f.label}</label>
                      {m && (
                        <span data-testid={`vin-mark-${f.key}`} className={`rounded-md border px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide ${m.cls}`}>
                          {mark === "corrected" && <Pencil className="w-2.5 h-2.5 inline mr-0.5 -mt-0.5" />}
                          {m.label}
                        </span>
                      )}
                      {meta[f.key]?.by && (
                        <span className="text-[10px] text-slate-400 font-mono">{meta[f.key].by.split("@")[0]} · {(meta[f.key].at || "").slice(0, 10)}</span>
                      )}
                    </div>
                    <div className="flex gap-2 mt-1">
                      <Input data-testid={`vin-input-${f.key}`} value={live} disabled={!canEdit}
                        onChange={(e) => setVal(f, e.target.value)} className="rounded-lg h-9" />
                      {canEdit && filled && !HUMAN.includes(mark) && (
                        <Button data-testid={`vin-confirm-${f.key}`} size="sm" variant="outline" disabled={busy}
                          onClick={() => run("confirm-decoded", { fields: [f.key] }, () => `${f.label} checked.`)}
                          className="rounded-lg h-9 shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-[11px]">
                          <Check className="w-3.5 h-3.5 mr-1" />That's right
                        </Button>
                      )}
                    </div>
                    {disagrees && (
                      <div data-testid={`vin-said-${f.key}`} className="mt-1 flex items-center gap-1.5 text-[11px] text-amber-700">
                        <Info className="w-3 h-3 shrink-0" />
                        The decode said <span className="font-mono font-semibold">{said[f.key]}</span>
                        {canEdit && (
                          <button data-testid={`vin-use-decode-${f.key}`} onClick={() => run("apply-decode", { fields: [f.key] }, () => `${f.label} set to the decode's value.`)}
                            className="underline text-[#B01E28]">use it</button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {canEdit && (
        <div className="mt-4 space-y-1.5">
          <div className="flex flex-wrap gap-2">
            <Button data-testid="vin-redecode" variant="outline" disabled={busy} onClick={() => run("apply-decode", {},
              (d) => `${d.applied.length} filled in, ${d.protected.length} left alone.`)}
              className="rounded-lg h-9">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ScanLine className="w-4 h-4 mr-1.5" />Re-decode VIN</>}
            </Button>
            {bulk.length > 0 && (
              <Button data-testid="vin-confirm-all" variant="outline" disabled={busy}
                onClick={() => run("confirm-decoded", { fields: bulk.map((f) => f.key) }, (d) => `${d.confirmed.length} checked.`)}
                className="rounded-lg h-9 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                <ShieldCheck className="w-4 h-4 mr-1.5" />Confirm {fieldList(bulk.map((f) => f.label))}
              </Button>
            )}
          </div>
          {unchecked.some((f) => f.own_tick) && (
            <p data-testid="vin-own-tick-note" className="text-[10px] text-amber-700">
              The engine and the transmission are ticked on their own, above — that's where a decode
              gets it wrong, so each one takes a look at the real thing.
            </p>
          )}
        </div>
      )}

      {report && (
        <div data-testid="vin-redecode-report" className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-[11px] space-y-1">
          {report.applied?.map((a) => (
            <div key={a.field} className="text-slate-700">Filled in <b>{a.label}</b>: {a.now}</div>
          ))}
          {report.protected?.map((p) => (
            <div key={p.field} data-testid={`vin-protected-${p.field}`} className="text-amber-800">
              Left <b>{p.label}</b> as <span className="font-mono">{p.kept || "(blank)"}</span> — {p.why}. The decode said <span className="font-mono">{p.decode_said}</span>.
            </div>
          ))}
          {!report.applied?.length && !report.protected?.length && <div className="text-slate-500">Nothing to change — the decode agrees with what's here.</div>}
        </div>
      )}
    </div>
  );
};
